import React, { useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { AlertTriangle, Info, XCircle } from 'lucide-react';

const UndoStepModal = ({ show, onClose, onConfirm, undoInfo }) => {
  const [reason, setReason] = useState('');

  const getWarningLevel = () => {
    if (undoInfo?.action === 'undo_verification') return 'danger';
    if (undoInfo?.action === 'undo_agi') return 'warning';
    return 'info';
  };

  const getWarningMessage = () => {
    switch (undoInfo?.action) {
      case 'undo_verification':
        return {
          title: 'Radera Bokföringsverifikat?',
          message: 'Detta kommer att:',
          impacts: [
            'Annullera verifikatet (om äldre än idag) eller radera helt (om skapat idag)',
            'Återställa löneperioden till steg 1',
            'Kräva att du skapar nytt verifikat vid nästa genomgång',
          ],
          warning: 'OBS: Om AGI redan är skickat till Skatteverket måste du ångra det först.'
        };
      case 'undo_agi':
        return {
          title: 'Ångra AGI-inlämning?',
          message: 'Detta kommer att:',
          impacts: [
            'Markera AGI som INTE inlämnad',
            'Återställa till steg 2 (Betala & Bokför)',
            'Kräva ny AGI-fil om något ändras',
          ],
          warning: 'OBS: Om AGI redan skickats till Skatteverket måste du hantera det manuellt!'
        };
      case 'undo_taxes':
        return {
          title: 'Ångra Skatteinbetalning?',
          message: 'Detta kommer att:',
          impacts: [
            'Markera skatter som INTE betalda',
            'Återställa till steg 3 (AGI Deklaration)',
          ],
          warning: 'OBS: Detta påverkar inte faktiska betalningar!'
        };
      default:
        return { title: 'Ångra?', message: '', impacts: [], warning: '' };
    }
  };

  const info = getWarningMessage();
  const isDangerous = undoInfo?.action === 'undo_verification';

  const handleConfirm = () => {
    onConfirm(reason);
    setReason(''); // Reset for next time
  };

  const handleClose = () => {
    setReason(''); // Reset for next time
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className={isDangerous ? 'border-danger' : 'border-warning'}>
        <Modal.Title className="d-flex align-items-center">
          {isDangerous ? (
            <XCircle size={24} className="me-2 text-danger" />
          ) : (
            <AlertTriangle size={24} className="me-2 text-warning" />
          )}
          {info.title}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant={getWarningLevel()} className="mb-3">
          <strong>{info.message}</strong>
          <ul className="mt-2 mb-0">
            {info.impacts.map((impact, idx) => (
              <li key={idx}>{impact}</li>
            ))}
          </ul>
        </Alert>

        {info.warning && (
          <Alert variant="danger" className="mb-3">
            <Info size={16} className="me-2" />
            {info.warning}
          </Alert>
        )}

        {/* Show current step context */}
        {undoInfo?.fromStep && undoInfo?.toStep && (
          <Alert variant="info" className="mb-3">
            <strong>Stegändring:</strong> Från {undoInfo.fromStep} till {undoInfo.toStep}
          </Alert>
        )}

        <div className="mb-3">
          <label className="form-label">
            <strong>Anledning (rekommenderas):</strong>
          </label>
          <textarea 
            className="form-control"
            rows="3"
            placeholder="Beskriv varför du ångrar detta steg..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="form-text">
            Detta sparas i revisionsloggen för framtida referens.
          </div>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Avbryt
        </Button>
        <Button 
          variant={getWarningLevel()} 
          onClick={handleConfirm}
          className="d-flex align-items-center"
        >
          {isDangerous ? (
            <XCircle size={16} className="me-2" />
          ) : (
            <AlertTriangle size={16} className="me-2" />
          )}
          Ja, jag är säker - Ångra
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UndoStepModal;
